// Pure day/hour math for the ladder portal. Asia/Makassar is UTC+8 with no DST, so all
// arithmetic is a fixed offset; Intl is only used for labels. Hour cells are starts 08..21.
export const TZ = "Asia/Makassar";
const OFFSET_MS = 8 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

export type CellState = 1 | 2 | 3;
/** playerId → (ISO starts_at from cellStartsAt → state). Absent = no. */
export type CellsByPlayer = Map<number, Map<string, CellState>>;

export const HOURS: number[] = Array.from({ length: 14 }, (_, i) => 8 + i);
export const HOUR_GROUPS: Array<{ label: string; hours: number[] }> = [
  { label: "Morning 08–12", hours: [8, 9, 10, 11] },
  { label: "Day 12–17", hours: [12, 13, 14, 15, 16] },
  { label: "Evening 17–22", hours: [17, 18, 19, 20, 21] },
];

const pad2 = (n: number) => String(n).padStart(2, "0");
const dateToYmd = (d: Date) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
const ymdToUtcNoon = (date: string) => { const [y, m, d] = date.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d, 12)); };

export const normalizeIso = (s: string): string => new Date(s).toISOString();
export const cellStartsAt = (date: string, hour: number): string => new Date(`${date}T${pad2(hour)}:00:00+08:00`).toISOString();
export const dayStartIso = (date: string): string => new Date(`${date}T00:00:00+08:00`).toISOString();

export function makassarParts(iso: string): { date: string; hour: number; minute: number; dow: number } {
  const shifted = new Date(new Date(iso).getTime() + OFFSET_MS);
  return { date: dateToYmd(shifted), hour: shifted.getUTCHours(), minute: shifted.getUTCMinutes(), dow: shifted.getUTCDay() };
}

export function addDaysIso(date: string, n: number): string {
  const d = ymdToUtcNoon(date); d.setUTCDate(d.getUTCDate() + n); return dateToYmd(d);
}
export function mondayOf(date: string): string {
  const dow = ymdToUtcNoon(date).getUTCDay(); // 0 Sun … 6 Sat
  return addDaysIso(date, dow === 0 ? -6 : 1 - dow);
}
export const weekDatesOf = (weekStart: string): string[] => Array.from({ length: 7 }, (_, i) => addDaysIso(weekStart, i));
export const isWeekend = (date: string): boolean => { const dow = ymdToUtcNoon(date).getUTCDay(); return dow === 0 || dow === 6; };
/** A booked match is over (and its score due) 90 minutes after it starts. */
export const matchOver = (startsAt: string, now: Date = new Date()): boolean => new Date(startsAt).getTime() + 90 * 60_000 < now.getTime();
export const todayMakassar = (now: Date = new Date()): string => makassarParts(now.toISOString()).date;

export function ladderWeekNo(startsOn: string, weekStart: string): number {
  const a = ymdToUtcNoon(mondayOf(startsOn)).getTime(), b = ymdToUtcNoon(weekStart).getTime();
  return Math.floor((b - a) / (7 * DAY_MS)) + 1;
}
export const weekStartForWeekNo = (startsOn: string, n: number): string => addDaysIso(mondayOf(startsOn), (n - 1) * 7);
export const seasonWeekStarts = (startsOn: string, weeksCount: number): string[] =>
  Array.from({ length: weeksCount }, (_, i) => weekStartForWeekNo(startsOn, i + 1));

/** Hours a slot touches, floored: 19:00–20:30 → [19, 20]; 20:00–21:30 → [20, 21]. */
export function slotHours(startsAt: string, endsAt: string): { date: string; hours: number[] } {
  const start = makassarParts(startsAt);
  const endMs = new Date(endsAt).getTime();
  const hours: number[] = [];
  for (let h = start.hour; h <= 23; h++) {
    const cellMs = new Date(cellStartsAt(start.date, h)).getTime();
    if (cellMs >= endMs) break;
    hours.push(h);
  }
  return { date: start.date, hours };
}

const stateAt = (cells: CellsByPlayer, pid: number, date: string, hour: number): CellState | 0 =>
  cells.get(pid)?.get(cellStartsAt(date, hour)) ?? 0;

/** A court the matcher can still offer: held by the club, not booked, and not proposed to any fixture. */
export const isFreeSlot = (s: { id: number; status: string; fixtureId: number | null }, takenSlotIds?: ReadonlySet<number>): boolean =>
  s.status === "held" && s.fixtureId === null && !takenSlotIds?.has(s.id);

export function slotFitsCells(slot: { startsAt: string; endsAt: string }, cells: CellsByPlayer, playerIds: number[]): boolean {
  const { date, hours } = slotHours(slot.startsAt, slot.endsAt);
  if (hours.length === 0 || playerIds.length === 0) return false;
  return playerIds.every((pid) => hours.every((h) => stateAt(cells, pid, date, h) === 1));
}

export function slotMaybeFits(slot: { startsAt: string; endsAt: string }, cells: CellsByPlayer, playerIds: number[]): { fits: boolean; maybeCount: number } {
  const { date, hours } = slotHours(slot.startsAt, slot.endsAt);
  if (hours.length === 0 || playerIds.length === 0) return { fits: false, maybeCount: 0 };
  let maybeCount = 0;
  for (const pid of playerIds) {
    let anyMaybe = false;
    for (const h of hours) {
      const s = stateAt(cells, pid, date, h);
      if (s !== 1 && s !== 2) return { fits: false, maybeCount: 0 };
      if (s === 2) anyMaybe = true;
    }
    if (anyMaybe) maybeCount++;
  }
  return { fits: maybeCount > 0, maybeCount };
}

/** Runs of ≥ 2 consecutive hours where every listed player is yes. endHour is exclusive. */
export function fourWayOverlaps(cells: CellsByPlayer, playerIds: number[], weekDates: string[]): Array<{ date: string; startHour: number; endHour: number }> {
  const out: Array<{ date: string; startHour: number; endHour: number }> = [];
  if (playerIds.length === 0) return out;
  for (const date of weekDates) {
    let run: number | null = null;
    for (const h of [...HOURS, 22]) {
      const allYes = h <= 21 && playerIds.every((pid) => stateAt(cells, pid, date, h) === 1);
      if (allYes && run === null) run = h;
      if (!allYes && run !== null) { if (h - run >= 2) out.push({ date, startHour: run, endHour: h }); run = null; }
    }
  }
  return out;
}

// ── Labels (en-GB, Makassar) ──────────────────────────────────────────────────
// Newer ICU renders en-GB September as "Sept"; the portal uses the 3-letter form everywhere.
const fmt = (iso: string, opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat("en-GB", { timeZone: TZ, ...opts }).format(new Date(iso)).replace(/\bSept\b/, "Sep");
export const fmtHour = (h: number): string => `${pad2(h)}:00`;
export const fmtHourRange = (a: number, b: number): string => `${pad2(a)}–${pad2(b)}`;
export const fmtDayDate = (date: string): string => fmt(cellStartsAt(date, 12), { weekday: "short", day: "numeric", month: "short" }).replace(",", "");
export const fmtDayShort = (date: string): string => fmt(cellStartsAt(date, 12), { weekday: "short" });
export const fmtDayNum = (date: string): string => fmt(cellStartsAt(date, 12), { day: "numeric", month: "short" });
export const fmtTime = (iso: string): string => fmt(iso, { hour: "2-digit", minute: "2-digit", hour12: false });
export const fmtDayTime = (iso: string): string => `${fmtDayDate(makassarParts(iso).date)} · ${fmtTime(iso)}`;
export const fmtTimeRange = (s: string, e: string): string => `${fmtTime(s)} – ${fmtTime(e)}`;
export const fmtWeekdayTime = (iso: string): string => `${fmt(iso, { weekday: "short" })} ${fmtTime(iso)}`;
export function fmtWeekRange(weekStart: string): string {
  const end = addDaysIso(weekStart, 6);
  const sameMonth = weekStart.slice(0, 7) === end.slice(0, 7);
  const dayOnly = (d: string) => fmt(cellStartsAt(d, 12), { day: "numeric" });
  return sameMonth ? `${dayOnly(weekStart)} – ${fmtDayNum(end)}` : `${fmtDayNum(weekStart)} – ${fmtDayNum(end)}`;
}

/** Half-hour match starts, 06:00 to 22:00 (what the "Set the time" own-booking picker offers; the server only accepts :00 and :30). */
/** Jungle Padel Sanur's courts, for a time or match booked outside the league slots. */
export const OWN_COURTS = ["1", "2", "3", "4", "5", "6", "7", "8"];
export const OWN_TIME_STARTS = Array.from({ length: 33 }, (_, i) => { const m = 6 * 60 + i * 30; return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`; });
